import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import {
  DEFAULT_PLATFORM,
  DEFAULT_DISPATCH,
  listenPlatformConfig,
  savePlatformConfig,
  countSearchingRides,
  getEnrolledFactors,
  startAdminTotpEnrollment,
  completeAdminTotpEnrollment,
  unenrollAdminMfa,
  getLaunchResetPreview,
  executeLaunchReset,
} from '../services/firebase';
import { useLang } from '../lang/LangContext';
import {
  SILENT_MIN_MINUTES,
  SILENT_MAX_MINUTES,
  SILENT_DEFAULT_MINUTES,
  readSilentSweepSettings,
  buildSilentSweepPayload,
  needsConfirm,
} from '../utils/silentSweepSettings';

const normalizePhone = (v) => String(v || '').replace(/\D/g, '');

export default function Settings({ isSuperAdmin = false }) {
  const { t } = useLang();
  const [form, setForm] = useState(DEFAULT_PLATFORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    setForm({ ...DEFAULT_PLATFORM, ...cfg });
    setLoading(false);
  }), []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const supportDigits = normalizePhone(form.supportPhone).slice(-10);
  const supportMissing = supportDigits.length !== 10;

  const save = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      toast.error('Modifying platform settings requires Super Admin permissions');
      return;
    }
    const phone = normalizePhone(form.supportPhone).slice(-10);
    if (phone.length !== 10) {
      toast.error('Support phone is required (10-digit mobile) — SOS “Call support” needs it');
      return;
    }
    let wa = normalizePhone(form.supportWhatsapp);
    if (wa.length === 10) wa = `91${wa}`;
    if (wa && wa.length < 11) {
      toast.error('WhatsApp should be 10 digits or 91XXXXXXXXXX');
      return;
    }

    // Fare fields: an empty box becomes Number('') === 0, which would collapse
    // every fare (base/perKm 0) or drop the floor (minimum 0). Block that here —
    // the apps read these live, so a bad save reaches every phone instantly.
    const fareBase = Number(form.fareBase);
    const farePerKm = Number(form.farePerKm);
    const fareMinimum = Number(form.fareMinimum);
    if (!Number.isFinite(fareBase) || fareBase <= 0 || fareBase > 500) {
      toast.error('Fare base must be a positive number (max 500)');
      return;
    }
    if (!Number.isFinite(farePerKm) || farePerKm <= 0 || farePerKm > 200) {
      toast.error('Fare / km must be a positive number (max 200)');
      return;
    }
    if (!Number.isFinite(fareMinimum) || fareMinimum <= 0 || fareMinimum > 1000) {
      toast.error('Minimum fare must be a positive number (max 1000)');
      return;
    }

    // Geo / radius fields: an empty box → NaN → a broken map centre or a
    // zero-radius service area. Keep the last good value on the doc instead.
    const geoNum = (v, fallback) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const serviceRadiusKm = geoNum(form.serviceRadiusKm, DEFAULT_PLATFORM.serviceRadiusKm);
    const nearbyCaptainRadiusKm = geoNum(form.nearbyCaptainRadiusKm, DEFAULT_PLATFORM.nearbyCaptainRadiusKm);
    if (serviceRadiusKm <= 0 || nearbyCaptainRadiusKm <= 0) {
      toast.error('Service radius and nearby-captain radius must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        city: form.city,
        state: form.state,
        mapCenterLat: geoNum(form.mapCenterLat, DEFAULT_PLATFORM.mapCenterLat),
        mapCenterLng: geoNum(form.mapCenterLng, DEFAULT_PLATFORM.mapCenterLng),
        serviceRadiusKm,
        nearbyCaptainRadiusKm,
        captainLocationMaxAgeSec: Number(form.captainLocationMaxAgeSec) || 180,
        supportPhone: phone,
        supportWhatsapp: wa || `91${phone}`,
        fareBase,
        farePerKm,
        fareMinimum,
      };
      await savePlatformConfig(payload);
      toast.success('Settings saved — apps pick this up live');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="empty"><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <>
    {!isSuperAdmin && (
      <div className="card alert-card" style={{ maxWidth: 720, marginBottom: 16, border: '1px solid #f59e0b', background: 'rgba(245, 158, 11, 0.08)' }}>
        <strong style={{ color: '#f59e0b' }}>🔍 {t('Reviewer Access (Read-Only)')}</strong>
        <p className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}>
          {t('You have read-only visibility into platform configuration and operational parameters. Modifying settings requires Super Admin privileges.')}
        </p>
      </div>
    )}
    <form className="card" onSubmit={save} style={{ maxWidth: 720 }}>
      <div className="card-title">{t('Platform settings')}</div>
      <p className="muted" style={{ marginBottom: 16 }}>
        {t('Changes apply to passenger & driver apps without redeploy (when signed in).')}
      </p>

      {supportMissing && (
        <div className="card alert-card" style={{ marginBottom: 16, border: '1px solid var(--red)' }}>
          <strong style={{ color: 'var(--red)' }}>{t('Support phone required')}</strong>
          <p className="muted" style={{ fontSize: 13, marginTop: 6 }}>
            {t('Passenger SOS → “Call MyTownly support” uses this number. Enter a 10-digit mobile below and save.')}
          </p>
        </div>
      )}

      <div className="settings-grid">
        <div className="form-group">
          <label className="form-label">CITY</label>
          <input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">STATE</label>
          <input type="text" value={form.state} onChange={(e) => set('state', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">MAP CENTER LAT</label>
          <input type="number" step="any" value={form.mapCenterLat} onChange={(e) => set('mapCenterLat', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">MAP CENTER LNG</label>
          <input type="number" step="any" value={form.mapCenterLng} onChange={(e) => set('mapCenterLng', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">SERVICE RADIUS (KM)</label>
          <input type="number" step="0.1" value={form.serviceRadiusKm} onChange={(e) => set('serviceRadiusKm', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">NEARBY CAPTAIN RADIUS (KM)</label>
          <input type="number" step="0.1" value={form.nearbyCaptainRadiusKm} onChange={(e) => set('nearbyCaptainRadiusKm', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">CAPTAIN GPS MAX AGE (SEC)</label>
          <input type="number" value={form.captainLocationMaxAgeSec || 180} onChange={(e) => set('captainLocationMaxAgeSec', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">FARE BASE (₹)</label>
          <input type="number" min="1" step="1" required value={form.fareBase} onChange={(e) => set('fareBase', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">FARE / KM (₹)</label>
          <input type="number" min="1" step="1" required value={form.farePerKm} onChange={(e) => set('farePerKm', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">MINIMUM FARE (₹)</label>
          <input type="number" min="1" step="1" required value={form.fareMinimum} onChange={(e) => set('fareMinimum', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">SUPPORT PHONE (REQUIRED) *</label>
          <input
            type="text"
            inputMode="numeric"
            required
            placeholder={t('10-digit mobile')}
            value={form.supportPhone}
            onChange={(e) => set('supportPhone', e.target.value)}
            style={supportMissing ? { borderColor: 'var(--red)' } : undefined}
          />
        </div>
        <div className="form-group">
          <label className="form-label">SUPPORT WHATSAPP</label>
          <input
            type="text"
            placeholder={t('91XXXXXXXXXX or 10 digits')}
            value={form.supportWhatsapp}
            onChange={(e) => set('supportWhatsapp', e.target.value)}
          />
        </div>
      </div>

      <button
        type="submit"
        className="btn btn-green"
        disabled={!isSuperAdmin || saving || supportMissing}
        style={{ marginTop: 8 }}
        title={!isSuperAdmin ? 'Requires Super Admin privileges' : ''}
      >
        {!isSuperAdmin ? t('🔒 Read-Only Mode') : saving ? t('Saving…') : t('Save settings')}
      </button>
    </form>
    <ZonesCard isSuperAdmin={isSuperAdmin} />
    <OperatingModeCard isSuperAdmin={isSuperAdmin} />
    <RequiredUpdateCard isSuperAdmin={isSuperAdmin} />
    <DispatchTuningCard isSuperAdmin={isSuperAdmin} />
    <SilentAnnaSweepCard isSuperAdmin={isSuperAdmin} />
    <NightOperationsCard isSuperAdmin={isSuperAdmin} />
    <RainOperationsCard isSuperAdmin={isSuperAdmin} />
    <WaitingChargeCard isSuperAdmin={isSuperAdmin} />
    <MfaSettingsCard />
    <LaunchResetCard isSuperAdmin={isSuperAdmin} />
    </>
  );
}

const LAUNCH_RESET_CONFIRMATION = 'RESET PRE-LAUNCH DATA';

function LaunchResetCard({ isSuperAdmin = false }) {
  const [preview, setPreview] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      setPreview(await getLaunchResetPreview());
    } catch (error) {
      console.error('launch reset preview failed', error);
      toast.error(error?.message || 'Could not load launch-reset preview');
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    if (!isSuperAdmin) {
      toast.error('Pre-launch data reset requires Super Admin permissions');
      return;
    }
    if (confirmation !== LAUNCH_RESET_CONFIRMATION) {
      toast.error(`Type exactly: ${LAUNCH_RESET_CONFIRMATION}`);
      return;
    }
    const ok = window.confirm(
      'FINAL CONFIRMATION\n\nThis one-time reset permanently deletes all pre-launch operational test data shown below and resets passenger/Anna trip and accounting counters.\n\nIt keeps profiles, KYC, approvals, settings, admin audit history, consent and deletion-request records.\n\nRun this only immediately before launch. Continue?',
    );
    if (!ok) return;
    setResetting(true);
    try {
      const result = await executeLaunchReset(confirmation);
      setPreview({ status: 'completed', completed: true, counts: result.counts });
      setConfirmation('');
      toast.success('Pre-launch data reset completed');
    } catch (error) {
      console.error('launch reset failed', error);
      toast.error(error?.message || 'Launch reset failed — it is now locked for safety');
      await loadPreview();
    } finally {
      setResetting(false);
    }
  };

  const status = preview?.status || 'not checked';
  const locked = preview?.completed || ['running', 'failed'].includes(preview?.status);
  const bookingsOpen = preview && preview.bookingsPaused === false;
  const rows = Object.entries(preview?.counts || {}).filter(([, count]) => Number(count) > 0);

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24, borderLeft: '4px solid #dc2626' }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🧹</span><span>One-time pre-launch data reset</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: locked ? '#64748b' : '#dc2626' }}>
          {status.toUpperCase()}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>
        Removes operational records created before the reset starts: rides and their private subrecords,
        ride shares, trails, SOS/support cases, broadcasts, test payments, and stale ride requests. It also
        resets passenger cancellation/history counters and Anna trip, earnings, and dues counters.
      </p>
      <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>
        It does <strong>not</strong> delete accounts, KYC/photos, approvals, settings, admins, consent/deletion
        records, or admin audit history. The reset locks permanently after it starts, including if it fails,
        so it cannot be accidentally re-run after launch. It also refuses to run while an Anna has a ride assigned
        or in progress.
      </p>

      {!preview && (
        <button type="button" className="btn btn-ghost" disabled={loading} onClick={loadPreview}>
          {loading ? 'Checking records…' : 'Preview pre-launch records'}
        </button>
      )}

      {preview?.completed && (
        <div className="alert-card" style={{ border: '1px solid #86efac', background: '#f0fdf4', color: '#166534' }}>
          This launch reset has already completed and is permanently locked.
        </div>
      )}

      {bookingsOpen && !locked && (
        <div className="alert-card" style={{ border: '1px solid #facc15', background: '#fefce8', color: '#854d0e', marginBottom: 14 }}>
          Pause new bookings in the <strong>Operating mode</strong> card above before this reset can run. After the reset completes, switch back to Normal for launch.
        </div>
      )}

      {preview && !locked && (
        <>
          <div style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
            <strong style={{ color: '#991b1b' }}>Preview — records that will be cleared</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px 16px', marginTop: 9, fontSize: 13 }}>
              {rows.length ? rows.map(([key, count]) => (
                <span key={key}>{key}: <strong>{count}</strong></span>
              )) : <span>No matching operational records found.</span>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">TYPE TO UNLOCK</label>
            <input
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder={LAUNCH_RESET_CONFIRMATION}
              autoComplete="off"
              disabled={resetting || !isSuperAdmin}
            />
          </div>
          <button
            type="button"
            className="btn btn-red"
            disabled={!isSuperAdmin || resetting || bookingsOpen || confirmation !== LAUNCH_RESET_CONFIRMATION}
            onClick={execute}
          >
            {!isSuperAdmin ? '🔒 Super Admin Required' : resetting ? 'Resetting launch data…' : 'Permanently reset pre-launch data'}
          </button>
        </>
      )}

      {preview && ['running', 'failed'].includes(preview.status) && (
        <div className="alert-card" style={{ border: '1px solid #facc15', background: '#fefce8', color: '#854d0e' }}>
          A launch reset was already started and is locked to avoid deleting any later launch records. Do not retry it from the panel.
        </div>
      )}
    </div>
  );
}

const VERSION_FORMAT = /^\d+(?:\.\d+){1,3}$/;

/**
 * Release gate configuration. It lives in config/platform, which Firestore
 * permits only for MFA-verified admins. Clients still fail open on malformed
 * data, while this UI prevents malformed data being saved in the first place.
 */
function RequiredUpdateCard({ isSuperAdmin = false }) {
  const defaults = DEFAULT_PLATFORM.releaseNotice;
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    setForm({ ...defaults, ...(cfg?.releaseNotice || {}) });
    setLoading(false);
  }), []);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const passengerTargeted = ['passenger', 'all'].includes(form.audience);
  const driverTargeted = ['driver', 'all'].includes(form.audience);

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Modifying required update settings requires Super Admin permissions');
      return;
    }
    const payload = {
      enabled: Boolean(form.enabled),
      type: 'required_update',
      audience: ['passenger', 'driver', 'all'].includes(form.audience) ? form.audience : 'all',
      title: String(form.title || '').trim().slice(0, 100),
      message: String(form.message || '').trim().slice(0, 500),
      passengerMinVersion: String(form.passengerMinVersion || '').trim(),
      driverMinVersion: String(form.driverMinVersion || '').trim(),
    };

    if (payload.enabled) {
      if (!payload.title || !payload.message) {
        toast.error('Add both a title and a message before enabling a required update');
        return;
      }
      if (passengerTargeted && !VERSION_FORMAT.test(payload.passengerMinVersion)) {
        toast.error('Enter a passenger minimum version such as 1.0.86');
        return;
      }
      if (driverTargeted && !VERSION_FORMAT.test(payload.driverMinVersion)) {
        toast.error('Enter an Anna minimum version such as 1.0.56');
        return;
      }
      const targets = payload.audience === 'all' ? 'Passenger and Anna' : payload.audience;
      const confirmed = window.confirm(
        `REQUIRED UPDATE\n\nThis puts a full-screen "Update now" gate in front of every ${targets} user below the version above, whenever they are not in an active ride, booking, or live offer.\n\nIf that version is not already live at 100% on Google Play production, "Update now" does nothing and those users are locked out until you fix it here.\n\nContinue?`,
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      await savePlatformConfig({ releaseNotice: payload });
      toast.success(payload.enabled ? 'Required update is live' : 'Required update disabled');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24, borderLeft: `4px solid ${form.enabled ? '#dc2626' : '#94a3b8'}` }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>⬆️</span><span>Required app update</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: form.enabled ? '#dc2626' : '#64748b' }}>
          {form.enabled ? 'ACTIVE' : 'OFF'}
        </span>
      </div>
      <div style={{
        fontSize: 13, marginBottom: 12, padding: '10px 12px', borderRadius: 10,
        background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', lineHeight: 1.5,
      }}>
        <strong>Set the minimum to a version already live at 100% on Google Play production.</strong>{' '}
        Pick a higher number and every user below it is stuck on a full-screen gate whose
        “Update now” does nothing — Play has no newer build to install. Ship first, gate second.
      </div>
      <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
        Older apps are blocked only while idle — never during a trip, passenger booking, or Anna’s
        open ride request. Title and message are shown to users exactly as typed (plain text).
      </p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontWeight: 700, marginBottom: 16 }}>
        <input type="checkbox" checked={Boolean(form.enabled)} onChange={(e) => set('enabled', e.target.checked)} disabled={!isSuperAdmin} />
        Enable required update
      </label>

      <div className="form-group">
        <label className="form-label">WHO MUST UPDATE</label>
        <select value={form.audience} onChange={(e) => set('audience', e.target.value)} disabled={!isSuperAdmin}>
          <option value="all">Passenger and Anna</option>
          <option value="passenger">Passenger only</option>
          <option value="driver">Anna only</option>
        </select>
      </div>

      <div className="form-grid-2">
        {passengerTargeted && (
          <div className="form-group">
            <label className="form-label">PASSENGER MINIMUM VERSION</label>
            <input value={form.passengerMinVersion} onChange={(e) => set('passengerMinVersion', e.target.value)} placeholder="e.g. 1.0.86" inputMode="decimal" disabled={!isSuperAdmin} />
          </div>
        )}
        {driverTargeted && (
          <div className="form-group">
            <label className="form-label">ANNA MINIMUM VERSION</label>
            <input value={form.driverMinVersion} onChange={(e) => set('driverMinVersion', e.target.value)} placeholder="e.g. 1.0.56" inputMode="decimal" disabled={!isSuperAdmin} />
          </div>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">TITLE</label>
        <input maxLength={100} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Update required" disabled={!isSuperAdmin} />
      </div>
      <div className="form-group">
        <label className="form-label">MESSAGE</label>
        <textarea rows={3} maxLength={500} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="A new version is ready with important reliability improvements." style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }} disabled={!isSuperAdmin} />
        <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>{form.message.length}/500</p>
      </div>

      <button
        type="button"
        className="btn btn-green"
        style={{ marginTop: 8, ...(form.enabled ? { background: '#dc2626' } : {}) }}
        disabled={!isSuperAdmin || saving}
        onClick={save}
      >
        {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : (form.enabled ? 'Save required update' : 'Save as disabled')}
      </button>
    </div>
  );
}

const MODES = [
  {
    id: 'normal',
    label: 'Normal',
    hint: 'Everything runs as usual.',
    color: '#34d399',
  },
  {
    id: 'paused',
    label: 'Pause new bookings',
    hint: 'Passengers can’t book. Rides already searching keep looking; rides in progress finish normally.',
    color: '#fbbf24',
  },
  {
    id: 'emergency',
    label: 'Emergency stop',
    hint: 'No new bookings AND every ride still only searching is cancelled with your message. Rides with a driver assigned / arrived / on trip are never touched.',
    color: '#f87171',
  },
];

/**
 * Operating mode — the single "open / pause / stop" switch. Writes
 * config/platform.mode + modeMessage (+ bookingEnabled mirror for the passenger
 * app). The Cloud Functions enforce it; emergency also triggers a server-side
 * cancel of every still-searching ride.
 */
function OperatingModeCard({ isSuperAdmin = false }) {
  const [mode, setMode] = useState('normal');
  const [savedMode, setSavedMode] = useState('normal');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    const m = ['normal', 'paused', 'emergency'].includes(cfg.mode)
      ? cfg.mode
      : (cfg.bookingEnabled === false ? 'paused' : 'normal');
    setMode(m);
    setSavedMode(m);
    setMessage(typeof cfg.modeMessage === 'string' ? cfg.modeMessage : '');
    setLoading(false);
  }), []);

  const dirty = mode !== savedMode || (mode !== 'normal');

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Modifying operating mode requires Super Admin permissions');
      return;
    }
    const trimmed = message.trim().slice(0, 240);
    if (mode !== 'normal' && !trimmed) {
      toast.error('Add a short message for passengers before pausing / stopping');
      return;
    }
    if (mode === 'emergency' && savedMode !== 'emergency') {
      let count = 0;
      try { count = await countSearchingRides(); } catch { /* best effort */ }
      const ok = window.confirm(
        `EMERGENCY STOP\n\nThis will immediately cancel ${count} ride${count === 1 ? '' : 's'} that ${count === 1 ? 'is' : 'are'} still searching for a driver, and block all new bookings.\n\nRides with a driver already assigned, arrived, or on trip will NOT be affected.\n\nContinue?`,
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await savePlatformConfig({
        mode,
        modeMessage: mode === 'normal' ? '' : trimmed,
        bookingEnabled: mode === 'normal',
      });
      toast.success(
        mode === 'normal' ? 'Back to normal — bookings open' : 'Saved — live within ~10 seconds',
      );
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  const active = MODES.find((m) => m.id === savedMode) || MODES[0];

  return (
    <div
      className="card"
      style={{ maxWidth: 720, marginTop: 24, borderLeft: `4px solid ${active.color}` }}
    >
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🚦</span><span>Operating mode</span>
        <span style={{
          marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: active.color,
          border: `1px solid ${active.color}`, borderRadius: 999, padding: '2px 10px',
        }}>
          {active.label.toUpperCase()}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
        The master control for whether MyTownly is taking rides. Changes are live within
        ~10 seconds. A trip that already has a driver is never interrupted, in any mode.
      </p>

      <div style={{ display: 'grid', gap: 10 }}>
        {MODES.map((m) => (
          <label
            key={m.id}
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
              padding: '12px 14px', borderRadius: 12,
              border: `1px solid ${mode === m.id ? m.color : 'var(--gray2, #333)'}`,
              background: mode === m.id ? `${m.color}18` : 'transparent',
            }}
          >
            <input
              type="radio" name="opmode" value={m.id}
              checked={mode === m.id}
              onChange={() => setMode(m.id)}
              disabled={!isSuperAdmin}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ fontWeight: 700, fontSize: 14, color: m.color }}>{m.label}</span>
              <span className="muted" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>{m.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {mode !== 'normal' && (
        <div className="form-group" style={{ marginTop: 14 }}>
          <label className="form-label">MESSAGE PASSENGERS SEE</label>
          <textarea
            rows={2}
            maxLength={240}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={!isSuperAdmin}
            placeholder={mode === 'emergency'
              ? 'e.g. MyTownly is paused due to heavy rain. Back shortly.'
              : 'e.g. Bookings resume at 6 AM.'}
            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
          />
          <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Shown when a passenger tries to book. {message.trim().length}/240
          </p>
        </div>
      )}

      <button
        type="button"
        className="btn btn-green"
        style={{ marginTop: 8, ...(mode === 'emergency' ? { background: '#dc2626' } : {}) }}
        disabled={!isSuperAdmin || saving || !dirty}
        onClick={save}
      >
        {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : (mode === 'emergency' ? 'Activate emergency stop' : 'Save operating mode')}
      </button>
    </div>
  );
}

const RING_MIN_M = 100;
const RING_MAX_M = 20000;
const HOLD_MIN_S = 1;
const HOLD_MAX_S = 30;
const GPS_MIN_S = 30;
const GPS_MAX_S = 900;
const BRIEF_DISCONNECT_MIN_S = 15;
const BRIEF_DISCONNECT_MAX_S = 120;
const FB_MIN_MIN = 1;
const FB_MAX_MIN = 60;

const clamp = (v, min, max, dflt) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return dflt;
  return Math.min(max, Math.max(min, Math.round(n)));
};

/**
 * Dispatch tuning — writes config/platform.dispatch (+ captainLocationMaxAgeSec).
 * The Cloud Function (firebase/functions/dispatch.js) re-clamps everything, so
 * a bad value here can never break matching; this card just keeps the UI honest.
 */
function DispatchTuningCard({ isSuperAdmin = false }) {
  const [gpsMaxAgeSec, setGpsMaxAgeSec] = useState(180);
  const [rings, setRings] = useState(DEFAULT_DISPATCH.rings);
  const [briefDisconnectGraceSec, setBriefDisconnectGraceSec] = useState(DEFAULT_DISPATCH.briefDisconnectGraceSec);
  const [fallbackMin, setFallbackMin] = useState(Math.round(DEFAULT_DISPATCH.fallbackMaxAgeSec / 60));
  const [fallbackEnabled, setFallbackEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    const d = { ...DEFAULT_DISPATCH, ...(cfg.dispatch || {}) };
    const list = Array.isArray(d.rings) && d.rings.length ? d.rings : DEFAULT_DISPATCH.rings;
    setRings(list.map((r) => ({
      radiusM: r && (r.radiusM == null) ? null : Number(r && r.radiusM) || null,
      holdMs: Number(r && r.holdMs) || 6000,
    })));
    setBriefDisconnectGraceSec(Number(d.briefDisconnectGraceSec) || DEFAULT_DISPATCH.briefDisconnectGraceSec);
    setFallbackMin(Math.round((Number(d.fallbackMaxAgeSec) || DEFAULT_DISPATCH.fallbackMaxAgeSec) / 60));
    setFallbackEnabled(d.fallbackEnabled !== false);
    setGpsMaxAgeSec(Number(cfg.captainLocationMaxAgeSec) || 180);
    setLoading(false);
  }), []);

  const setRing = (i, patch) => setRings((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const resetDefaults = () => {
    setRings(DEFAULT_DISPATCH.rings);
    setBriefDisconnectGraceSec(DEFAULT_DISPATCH.briefDisconnectGraceSec);
    setFallbackMin(Math.round(DEFAULT_DISPATCH.fallbackMaxAgeSec / 60));
    setFallbackEnabled(true);
    setGpsMaxAgeSec(180);
  };

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Modifying dispatch settings requires Super Admin permissions');
      return;
    }
    setSaving(true);
    try {
      const cleanRings = rings.map((r, i) => ({
        radiusM: r.radiusM == null
          ? null
          : clamp(r.radiusM, RING_MIN_M, RING_MAX_M, DEFAULT_DISPATCH.rings[Math.min(i, 3)].radiusM || 4000),
        holdMs: clamp(r.holdMs / 1000, HOLD_MIN_S, HOLD_MAX_S, 6) * 1000,
      }));
      await savePlatformConfig({
        captainLocationMaxAgeSec: clamp(gpsMaxAgeSec, GPS_MIN_S, GPS_MAX_S, 180),
        dispatch: {
          rings: cleanRings,
          briefDisconnectGraceSec: clamp(
            briefDisconnectGraceSec,
            BRIEF_DISCONNECT_MIN_S,
            BRIEF_DISCONNECT_MAX_S,
            DEFAULT_DISPATCH.briefDisconnectGraceSec,
          ),
          fallbackMaxAgeSec: clamp(fallbackMin, FB_MIN_MIN, FB_MAX_MIN, 25) * 60,
          fallbackEnabled: !!fallbackEnabled,
        },
      });
      toast.success('Dispatch settings saved — live within ~10 seconds');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🎯</span><span>Dispatch tuning</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        How the app searches for a driver when a ride is booked. Changes are live within
        ~10 seconds — no app update. Leave these at the defaults unless you have a specific
        reason; every value is also re-checked on the server so a mistake can’t break matching.
      </p>

      <div className="form-group">
        <label className="form-label">DRIVER GPS “FRESH” CUTOFF (SECONDS)</label>
        <input
          type="number" min={GPS_MIN_S} max={GPS_MAX_S}
          value={gpsMaxAgeSec}
          onChange={(e) => setGpsMaxAgeSec(e.target.value)}
          disabled={!isSuperAdmin}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          A driver’s location must be newer than this to be offered a normal ride
          (default 180). Also drives the passenger’s “Anna’s location is old” notice.
        </p>
      </div>

      <label className="form-label" style={{ display: 'block', marginTop: 8 }}>SEARCH RINGS</label>
      <p className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>
        Dispatch offers the ride to drivers within Ring 1, waits, then widens to Ring 2, and so on.
      </p>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr><th>RING</th><th>RADIUS (METRES)</th><th>WAIT (SECONDS)</th></tr>
          </thead>
          <tbody>
            {rings.map((r, i) => (
              <tr key={i}>
                <td className="mono">Ring {i + 1}{i === rings.length - 1 ? ' (final)' : ''}</td>
                <td>
                  {r.radiusM == null ? (
                    <span className="muted">Whole service area</span>
                  ) : (
                    <input
                      type="number" min={RING_MIN_M} max={RING_MAX_M} step={100}
                      style={{ width: 120 }}
                      value={r.radiusM}
                      onChange={(e) => setRing(i, { radiusM: Number(e.target.value) })}
                      disabled={!isSuperAdmin}
                    />
                  )}
                </td>
                <td>
                  <input
                    type="number" min={HOLD_MIN_S} max={HOLD_MAX_S}
                    style={{ width: 90 }}
                    value={Math.round(r.holdMs / 1000)}
                    onChange={(e) => setRing(i, { holdMs: Number(e.target.value) * 1000 })}
                    disabled={!isSuperAdmin}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="form-group" style={{ marginTop: 16 }}>
        <label className="form-label">BRIEF CONNECTION-DROP GRACE (SECONDS)</label>
        <input
          type="number" min={BRIEF_DISCONNECT_MIN_S} max={BRIEF_DISCONNECT_MAX_S}
          value={briefDisconnectGraceSec}
          onChange={(e) => setBriefDisconnectGraceSec(e.target.value)}
          disabled={!isSuperAdmin}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          An online Anna with a fresh GPS fix can still receive her normal-ring offer for this
          short period after a network hand-off. Older disconnected locations remain fallback-only.
        </p>
      </div>

      <div className="form-group" style={{ marginTop: 16 }}>
        <label className="form-label">LAST-RESORT FALLBACK</label>
        <p className="muted" style={{ fontSize: 12, margin: '2px 0 8px' }}>
          If no driver with fresh GPS is found anywhere, offer the ride to a parked driver
          whose location went quiet — as long as it’s newer than this.
        </p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={fallbackEnabled}
              onChange={(e) => setFallbackEnabled(e.target.checked)}
              disabled={!isSuperAdmin}
            />
            Fallback enabled
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            Location max age
            <input
              type="number" min={FB_MIN_MIN} max={FB_MAX_MIN}
              style={{ width: 70 }}
              disabled={!fallbackEnabled || !isSuperAdmin}
              value={fallbackMin}
              onChange={(e) => setFallbackMin(e.target.value)}
            />
            minutes
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving} onClick={save}>
          {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save dispatch settings'}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" disabled={!isSuperAdmin || saving} onClick={resetDefaults}>
          Reset to defaults
        </button>
      </div>
    </div>
  );
}

/**
 * Silent Annas — writes config/platform.silentOfflineMinutes and .silentOfflinePush (top-level
 * fields, NOT inside `dispatch`: saving Dispatch tuning replaces that whole block).
 * The server (functions/silentOnlineSweep.js) re-reads them on every 15-minute run, so a save
 * takes effect at the next check. Reading and saving rules live in utils/silentSweepSettings.js.
 */
function SilentAnnaSweepCard({ isSuperAdmin = false }) {
  const [saved, setSaved] = useState(() => readSilentSweepSettings(null));
  const [enabled, setEnabled] = useState(true);
  const [minutesText, setMinutesText] = useState(String(SILENT_DEFAULT_MINUTES));
  const [pushEnabled, setPushEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    const s = readSilentSweepSettings(cfg);
    setSaved(s);
    setEnabled(s.enabled);
    setMinutesText(String(s.minutes));
    setPushEnabled(s.pushEnabled);
    setLoading(false);
  }), []);

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Changing this requires Super Admin permissions');
      return;
    }
    const result = buildSilentSweepPayload({ enabled, minutesText, pushEnabled });
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    // Turning it on, or shortening the time, can switch Annas off at the very next check.
    if (needsConfirm(saved, result.payload)
      && !window.confirm(`Annas whose phone has been silent for ${result.payload.silentOfflineMinutes} minutes or more will be switched offline at the next check (every 15 minutes). Continue?`)) {
      return;
    }
    setSaving(true);
    try {
      await savePlatformConfig(result.payload);
      toast.success('Saved — takes effect at the next check (within 15 minutes)');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>📴</span><span>Silent Annas (automatic offline)</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        An Anna can stay “online” after his phone dies or the app is killed. Every 15 minutes the
        server switches offline any Anna who is marked online but has shown no sign of life (no
        GPS, no app activity) for the time below. He can tap Go Online again himself. An Anna on
        a ride or an Intertown trip is never switched off. A save takes effect at the next check.
      </p>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox" checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!isSuperAdmin}
          />
          Switch silent Annas offline automatically
        </label>
      </div>

      <div className="form-group">
        <label className="form-label">SILENT FOR THIS LONG (MINUTES)</label>
        <input
          type="number" min={SILENT_MIN_MINUTES} max={SILENT_MAX_MINUTES} step={1}
          style={{ width: 120 }}
          value={minutesText}
          onChange={(e) => setMinutesText(e.target.value)}
          disabled={!enabled || !isSuperAdmin}
        />
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Default {SILENT_DEFAULT_MINUTES}. From {SILENT_MIN_MINUTES} to {SILENT_MAX_MINUTES} (12 hours).
          Shorter switches Annas off sooner but can catch a phone that only lost signal for a while.
          The first check after saving can switch off every Anna who is already that quiet.
        </p>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox" checked={pushEnabled}
            onChange={(e) => setPushEnabled(e.target.checked)}
            disabled={!enabled || !isSuperAdmin}
          />
          Send him a notification when he is switched off
        </label>
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Telugu and English: “You were taken offline. Open the app and tap Go Online to get rides.”
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving} onClick={save}>
          {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save silent-Anna settings'}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          Saved now: {saved.enabled ? `on, after ${saved.minutes} minutes` : 'off'}
          {saved.enabled ? ` · notification ${saved.pushEnabled ? 'on' : 'off'}` : ''}
        </span>
      </div>
    </div>
  );
}

const fmtHour = (h) => `${String(h % 12 === 0 ? 12 : h % 12).padStart(2, '0')}:00 ${h < 12 ? 'AM' : 'PM'}`;

/**
 * Night operating window — writes config/platform.night*. Who may drive
 * (permitEnabled, gated per-Anna from the Drivers page) and the fare
 * surcharge (pricingEnabled) are independent switches sharing one window.
 */
function NightOperationsCard({ isSuperAdmin = false }) {
  const [permitEnabled, setPermitEnabled] = useState(false);
  const [pricingEnabled, setPricingEnabled] = useState(false);
  const [startHour, setStartHour] = useState(DEFAULT_PLATFORM.nightStartHour);
  const [endHour, setEndHour] = useState(DEFAULT_PLATFORM.nightEndHour);
  const [fareMultiplier, setFareMultiplier] = useState(DEFAULT_PLATFORM.nightFareMultiplier);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    setPermitEnabled(cfg.nightPermitEnabled === true);
    setPricingEnabled(cfg.nightPricingEnabled === true);
    setStartHour(Number.isInteger(cfg.nightStartHour) ? cfg.nightStartHour : DEFAULT_PLATFORM.nightStartHour);
    setEndHour(Number.isInteger(cfg.nightEndHour) ? cfg.nightEndHour : DEFAULT_PLATFORM.nightEndHour);
    setFareMultiplier(Number(cfg.nightFareMultiplier) || DEFAULT_PLATFORM.nightFareMultiplier);
    setLoading(false);
  }), []);

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Modifying night operations requires Super Admin permissions');
      return;
    }
    setSaving(true);
    try {
      await savePlatformConfig({
        nightPermitEnabled: !!permitEnabled,
        nightPricingEnabled: !!pricingEnabled,
        nightStartHour: clamp(startHour, 0, 23, DEFAULT_PLATFORM.nightStartHour),
        nightEndHour: clamp(endHour, 0, 23, DEFAULT_PLATFORM.nightEndHour),
        nightFareMultiplier: Math.min(5, Math.max(1, Number(fareMultiplier) || DEFAULT_PLATFORM.nightFareMultiplier)),
      });
      toast.success('Night operations saved — live within ~10 seconds');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🌙</span><span>Night operations</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Restrict who may drive and/or charge a surcharge during a nightly window (IST). Both
        switches default off and change nothing until you turn them on here.
      </p>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={permitEnabled} onChange={(e) => setPermitEnabled(e.target.checked)} disabled={!isSuperAdmin} />
          Require a night permit
        </label>
        <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
          Only Annas granted a permit (Drivers page → 🌙 Night: ON) can be offered or accept a ride
          during the window below. Everyone else drives normally outside it — nothing else changes.
        </p>
      </div>

      <div className="form-group" style={{ marginTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={pricingEnabled} onChange={(e) => setPricingEnabled(e.target.checked)} disabled={!isSuperAdmin} />
          Apply a night fare surcharge
        </label>
        <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
          Multiplies the normal fare for rides booked inside the window below.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
        <div className="form-group">
          <label className="form-label">WINDOW START (HOUR, IST)</label>
          <input
            type="number" min={0} max={23} style={{ width: 90 }}
            value={startHour}
            onChange={(e) => setStartHour(Number(e.target.value))}
            disabled={!isSuperAdmin}
          />
        </div>
        <div className="form-group">
          <label className="form-label">WINDOW END (HOUR, IST)</label>
          <input
            type="number" min={0} max={23} style={{ width: 90 }}
            value={endHour}
            onChange={(e) => setEndHour(Number(e.target.value))}
            disabled={!isSuperAdmin}
          />
        </div>
        <div className="form-group">
          <label className="form-label">NIGHT FARE MULTIPLIER</label>
          <input
            type="number" min={1} max={5} step={0.1}
            style={{ width: 90 }}
            disabled={!pricingEnabled || !isSuperAdmin}
            value={fareMultiplier}
            onChange={(e) => setFareMultiplier(e.target.value)}
          />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
        {startHour === endHour
          ? 'Start and end are the same hour — treated as no window at all (safe default), not "always night".'
          : `Window: ${fmtHour(startHour)} – ${fmtHour(endHour)}${startHour > endHour ? ' (crosses midnight)' : ''}`}
      </p>

      <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving} onClick={save} style={{ marginTop: 16 }}>
        {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save night operations'}
      </button>
    </div>
  );
}

/**
 * Rain surcharge — writes config/platform.rain*. Deliberately a manual
 * switch, not a weather API: flip it on when it starts raining, off when it
 * stops. No time window (unlike night); independent of the night surcharge —
 * both can be active at once and stack.
 */
function RainOperationsCard({ isSuperAdmin = false }) {
  const [enabled, setEnabled] = useState(false);
  const [fareMultiplier, setFareMultiplier] = useState(DEFAULT_PLATFORM.rainFareMultiplier);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    setEnabled(cfg.rainModeEnabled === true);
    setFareMultiplier(Number(cfg.rainFareMultiplier) || DEFAULT_PLATFORM.rainFareMultiplier);
    setLoading(false);
  }), []);

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Modifying rain operations requires Super Admin permissions');
      return;
    }
    setSaving(true);
    try {
      await savePlatformConfig({
        rainModeEnabled: !!enabled,
        rainFareMultiplier: Math.min(5, Math.max(1, Number(fareMultiplier) || DEFAULT_PLATFORM.rainFareMultiplier)),
      });
      toast.success('Rain operations saved — live within ~10 seconds');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🌧️</span><span>Rain operations</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Charge a surcharge while it is actually raining. There is no automatic weather
        detection — turn this on when it starts raining and off once it stops.
      </p>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!isSuperAdmin} />
          Apply a rain fare surcharge right now
        </label>
        <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
          Multiplies the normal fare for every ride booked while this is on — including
          alongside a night surcharge, if that is active too.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
        <div className="form-group">
          <label className="form-label">RAIN FARE MULTIPLIER</label>
          <input
            type="number" min={1} max={5} step={0.1}
            style={{ width: 90 }}
            disabled={!enabled || !isSuperAdmin}
            value={fareMultiplier}
            onChange={(e) => setFareMultiplier(e.target.value)}
          />
        </div>
      </div>

      <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving} onClick={save} style={{ marginTop: 16 }}>
        {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save rain operations'}
      </button>
    </div>
  );
}

const WAITING_LIMITS = {
  waitingFreeMinutes: { label: 'Free minutes', min: 0, max: 15 },
  waitingPerMinute: { label: '₹ per minute', min: 0, max: 10 },
  waitingMaxCharge: { label: 'Maximum charge (₹)', min: 0, max: 50 },
};

/** A whole number within the limits, or null. A typed 0 is kept (never swapped for a default). */
const wholeWithin = (value, { min, max }) => {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < min || n > max) return null;
  return n;
};

/** Mirrors the server: each started minute after the free time counts, up to the cap. */
const exampleWaitingCharge = (waitedMin, free, rate, cap) => (
  Math.min(cap, Math.max(0, waitedMin - free) * rate)
);

/**
 * Pickup waiting charge — writes config/platform.waiting*.
 *
 * The clock starts when the Anna taps Arrived (only if his phone proves he is at
 * the pickup) and stops when the rider's trip PIN is accepted. All of it goes
 * to the Anna. Riders on older app versions are never charged.
 *   Off    nothing happens
 *   Quiet  records what would have been charged, charges nobody — run this first
 *   On     charges riders who accepted the terms (needs the updated rider app)
 * Switching to Off stops charging immediately, even for rides already waiting.
 */
function WaitingChargeCard({ isSuperAdmin = false }) {
  const [savedMode, setSavedMode] = useState('off');
  const [mode, setMode] = useState('off');
  const [fields, setFields] = useState({
    waitingFreeMinutes: DEFAULT_PLATFORM.waitingFreeMinutes,
    waitingPerMinute: DEFAULT_PLATFORM.waitingPerMinute,
    waitingMaxCharge: DEFAULT_PLATFORM.waitingMaxCharge,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    const m = ['off', 'quiet', 'on'].includes(cfg.waitingMode) ? cfg.waitingMode : 'off';
    setSavedMode(m);
    setMode(m);
    setFields({
      waitingFreeMinutes: cfg.waitingFreeMinutes ?? DEFAULT_PLATFORM.waitingFreeMinutes,
      waitingPerMinute: cfg.waitingPerMinute ?? DEFAULT_PLATFORM.waitingPerMinute,
      waitingMaxCharge: cfg.waitingMaxCharge ?? DEFAULT_PLATFORM.waitingMaxCharge,
    });
    setLoading(false);
  }), []);

  const parsed = Object.fromEntries(
    Object.entries(WAITING_LIMITS).map(([key, limits]) => [key, wholeWithin(fields[key], limits)]),
  );
  const invalid = Object.entries(parsed).find(([, v]) => v === null);

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Changing waiting charges requires Super Admin permissions');
      return;
    }
    if (invalid) {
      const { label, min, max } = WAITING_LIMITS[invalid[0]];
      toast.error(`${label}: enter a whole number from ${min} to ${max}`);
      return;
    }
    if (mode === 'on' && savedMode !== 'on'
      && !window.confirm('Turn waiting charges ON? Riders on the updated app will be charged real money for waiting after the free minutes.')) {
      return;
    }
    setSaving(true);
    try {
      await savePlatformConfig({
        waitingMode: mode,
        waitingFreeMinutes: parsed.waitingFreeMinutes,
        waitingPerMinute: parsed.waitingPerMinute,
        waitingMaxCharge: parsed.waitingMaxCharge,
      });
      toast.success('Waiting charge saved — live within ~10 seconds');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const setField = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));
  const modes = [
    ['off', 'Off', 'Nothing is recorded or charged.'],
    ['quiet', 'Quiet (record only)', 'Records what would have been charged on every ride. Nobody is charged. Use this first.'],
    ['on', 'On', 'Charges riders who accepted the terms. Needs the updated rider app.'],
  ];
  const ok = !invalid;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>⏱️</span><span>Pickup waiting charge</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        When an Anna waits at the pickup, the rider gets some minutes free, then pays a small
        per-minute charge up to a cap — all of it goes to the Anna. The clock starts when the Anna
        taps Arrived (only if his phone proves he is at the pickup) and stops when the rider's trip
        PIN is accepted. Riders on older app versions are never charged.
      </p>

      <div className="form-group">
        {modes.map(([value, label, help]) => (
          <label key={value} style={{ display: 'block', fontSize: 13, marginBottom: 8 }}>
            <input
              type="radio" name="waitingMode" value={value}
              checked={mode === value} disabled={!isSuperAdmin}
              onChange={() => setMode(value)}
            />{' '}
            <strong>{label}</strong>
            <span className="muted" style={{ display: 'block', fontSize: 12, marginLeft: 22 }}>{help}</span>
          </label>
        ))}
        <p className="muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
          Switching to Off stops charging at once — even for a ride that is already waiting.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
        {Object.entries(WAITING_LIMITS).map(([key, { label, min, max }]) => (
          <div className="form-group" key={key}>
            <label className="form-label">{label.toUpperCase()}</label>
            <input
              type="number" min={min} max={max} step={1}
              style={{ width: 110, ...(parsed[key] === null ? { borderColor: '#e53e3e' } : {}) }}
              disabled={!isSuperAdmin}
              value={fields[key]}
              onChange={setField(key)}
            />
          </div>
        ))}
      </div>

      {ok && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Example: waiting 5 min → ₹{exampleWaitingCharge(5, parsed.waitingFreeMinutes, parsed.waitingPerMinute, parsed.waitingMaxCharge)} ·
          {' '}10 min → ₹{exampleWaitingCharge(10, parsed.waitingFreeMinutes, parsed.waitingPerMinute, parsed.waitingMaxCharge)} ·
          {' '}30 min → ₹{exampleWaitingCharge(30, parsed.waitingFreeMinutes, parsed.waitingPerMinute, parsed.waitingMaxCharge)}.
          Each started minute counts. A ₹0 rate or ₹0 maximum means no charge.
        </p>
      )}
      {mode !== savedMode && (
        <p style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
          Unsaved change: {savedMode} → {mode}
        </p>
      )}

      <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving || !ok} onClick={save} style={{ marginTop: 16 }}>
        {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save waiting charge'}
      </button>
    </div>
  );
}

/**
 * Extra towns beyond the default city/map-center in the form above (writes
 * config/platform.zones). Each zone is its own circle with its own radius —
 * same fare everywhere, a zone only ever changes WHERE a passenger can book,
 * never the price. "Live" off shows a "coming soon" message to anyone
 * standing inside that zone instead of the normal booking screen — meant for
 * a town you've set up but don't have Annas in yet.
 */
function ZonesCard({ isSuperAdmin = false }) {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => listenPlatformConfig((cfg) => {
    setZones((Array.isArray(cfg.zones) ? cfg.zones : []).map((z) => ({
      name: z?.name || '',
      lat: z?.center?.lat ?? '',
      lng: z?.center?.lng ?? '',
      radiusKm: z?.radiusKm ?? '',
      active: z?.active !== false,
    })));
    setLoading(false);
  }), []);

  const updateZone = (index, field, value) => {
    setZones((list) => list.map((z, i) => (i === index ? { ...z, [field]: value } : z)));
  };
  const addZone = () => {
    setZones((list) => [...list, { name: '', lat: '', lng: '', radiusKm: 5, active: false }]);
  };
  const removeZone = (index) => {
    setZones((list) => list.filter((_, i) => i !== index));
  };
  const slugify = (name) => String(name).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const save = async () => {
    if (!isSuperAdmin) {
      toast.error('Modifying service zones requires Super Admin permissions');
      return;
    }
    const cleaned = [];
    const usedIds = new Set();
    for (const z of zones) {
      const name = String(z.name || '').trim();
      const lat = Number(z.lat);
      const lng = Number(z.lng);
      const radiusKm = Number(z.radiusKm);
      if (!name) { toast.error('Every zone needs a town name'); return; }
      if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
        toast.error(`${name}: latitude/longitude look wrong`);
        return;
      }
      if (!Number.isFinite(radiusKm) || radiusKm <= 0 || radiusKm > 50) {
        toast.error(`${name}: service radius must be greater than 0 (max 50km)`);
        return;
      }
      const base = slugify(name) || 'zone';
      let id = base;
      let suffix = 2;
      while (usedIds.has(id)) { id = `${base}-${suffix}`; suffix += 1; }
      usedIds.add(id);
      cleaned.push({ id, name, center: { lat, lng }, radiusKm, active: !!z.active });
    }

    setSaving(true);
    try {
      await savePlatformConfig({ zones: cleaned });
      toast.success('Zones saved — apps pick this up live');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🗺️</span><span>Service zones</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Extra towns beyond the default city above. Each one gets its own map circle —
        passengers there see and book within just that circle. Same fares everywhere;
        a zone only changes WHERE someone can book, never the price.
      </p>

      {zones.length === 0 && (
        <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>No extra towns yet — add one below.</p>
      )}

      {zones.map((zone, index) => (
        <div key={index} className="card" style={{ background: 'var(--card-hover)', marginBottom: 12, padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label className="form-label">TOWN NAME</label>
              <input type="text" placeholder="e.g. Kalikiri" value={zone.name} onChange={(e) => updateZone(index, 'name', e.target.value)} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">MAP CENTER LAT</label>
              <input type="number" step="any" value={zone.lat} onChange={(e) => updateZone(index, 'lat', e.target.value)} style={{ width: 120 }} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">MAP CENTER LNG</label>
              <input type="number" step="any" value={zone.lng} onChange={(e) => updateZone(index, 'lng', e.target.value)} style={{ width: 120 }} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label className="form-label">SERVICE RADIUS (KM)</label>
              <input type="number" step="0.1" min="0.1" max="50" value={zone.radiusKm} onChange={(e) => updateZone(index, 'radiusKm', e.target.value)} style={{ width: 90 }} disabled={!isSuperAdmin} />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={zone.active} onChange={(e) => updateZone(index, 'active', e.target.checked)} disabled={!isSuperAdmin} />
                Live (bookable)
              </label>
            </div>
            {isSuperAdmin && <button type="button" className="btn btn-red btn-sm" onClick={() => removeZone(index)}>Remove</button>}
          </div>
          {!zone.active && (
            <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
              Not live yet — anyone here sees a “coming soon” message instead of the booking screen.
            </p>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        {isSuperAdmin && <button type="button" className="btn btn-ghost" onClick={addZone}>+ Add a town</button>}
        <button type="button" className="btn btn-green" disabled={!isSuperAdmin || saving} onClick={save}>
          {!isSuperAdmin ? '🔒 Read-Only' : saving ? 'Saving…' : 'Save zones'}
        </button>
      </div>
      <p className="muted" style={{ fontSize: 11, marginTop: 10 }}>
        Tip: right-click a spot on Google Maps and the coordinates show up ready to copy.
      </p>
    </div>
  );
}

function MfaSettingsCard() {
  const [factors, setFactors] = useState([]);
  const [secret, setSecret] = useState(null);
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const refreshFactors = () => {
    setFactors(getEnrolledFactors());
  };

  useEffect(() => {
    refreshFactors();
  }, []);

  const resetSetup = () => {
    setSecret(null);
    setOtpauthUrl('');
    setSecretKey('');
    setCode('');
  };

  const handleStart = async () => {
    setBusy(true);
    try {
      const r = await startAdminTotpEnrollment();
      setSecret(r.secret);
      setOtpauthUrl(r.otpauthUrl);
      setSecretKey(r.secretKey);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Could not start authenticator setup');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyAndEnroll = async () => {
    if (code.trim().length < 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    setBusy(true);
    try {
      await completeAdminTotpEnrollment(secret, code, 'Authenticator app');
      toast.success('🎉 Authenticator app enrolled successfully');
      resetSetup();
      refreshFactors();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'That code did not match — try the current one');
    } finally {
      setBusy(false);
    }
  };

  const handleUnenroll = async (factorUid) => {
    if (!window.confirm('Remove this 2FA factor? Admin access requires 2FA — you will be blocked from the panel and forced to re-enroll immediately.')) return;
    setBusy(true);
    try {
      await unenrollAdminMfa(factorUid);
      toast.success('Factor removed — reloading…');
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.message || 'Failed to unenroll');
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: 720, marginTop: 24 }}>
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🔐</span>
        <span>Admin Multi-Factor Authentication (2FA)</span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Every admin login requires a 6-digit code from an authenticator app
        (Google Authenticator, Microsoft Authenticator, Authy, 1Password).
      </p>

      {factors.length > 0 ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {factors.map((f) => (
            <div
              key={f.uid}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                background: 'rgba(0, 168, 107, 0.08)',
                border: '1px solid rgba(0, 168, 107, 0.25)',
                borderRadius: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: '#34d399', fontSize: 14 }}>
                  🟢 2FA active: {f.displayName || 'Authenticator app'}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {f.factorId === 'totp' ? 'Authenticator app' : (f.phoneNumber || 'Registered factor')} · ID: {f.uid.slice(0, 12)}…
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                disabled={busy}
                onClick={() => handleUnenroll(f.uid)}
              >
                Disable 2FA
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: 18, borderRadius: 12, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, color: '#fb923c', fontSize: 13, marginBottom: 12 }}>
            ⚠️ 2FA is not yet enrolled on this admin account
          </div>

          {!otpauthUrl ? (
            <button
              type="button"
              className="btn btn-green"
              disabled={busy}
              onClick={handleStart}
            >
              {busy ? 'Preparing…' : 'Set up authenticator app'}
            </button>
          ) : (
            <div>
              <div style={{ background: '#fff', padding: 12, borderRadius: 12, width: 'fit-content', marginBottom: 12 }}>
                <QRCodeSVG value={otpauthUrl} size={172} />
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Can&rsquo;t scan? Enter this key in your app:</div>
              <code style={{ display: 'block', fontSize: 12.5, wordBreak: 'break-all', background: 'rgba(255,255,255,0.05)', padding: '8px 10px', borderRadius: 8, marginBottom: 12, maxWidth: 340 }}>
                {secretKey}
              </code>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  maxLength="6"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  style={{
                    width: 140,
                    fontSize: 20,
                    letterSpacing: 4,
                    textAlign: 'center',
                    fontFamily: 'JetBrains Mono',
                    fontWeight: 700,
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-green"
                  disabled={busy || code.length < 6}
                  onClick={handleVerifyAndEnroll}
                >
                  {busy ? 'Enrolling…' : 'Verify & enable 2FA →'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy}
                  onClick={resetSetup}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
